import moment from 'moment';

export const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
export const REPORT_AVAILABLE_DATE_REPLACEMENT = '{{report_available_date}}';
export const SURVEY_NAME_REPLACEMENT = '{{survey_name}}';
export const REPORT_NAME_REPLACEMENT = '{{report_name}}';
export const CLOSE_DATE_REPLACEMENT = '{{close_date}}';

export const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE;

const DOMAIN = process.env.REACT_APP_DOMAIN;
const PRODUCT_WINDOW_LENGTH = 12; // weeks

export const ENDPOINTS = {
  AUTH: `${DOMAIN}api/users/send-authcode`,
  USER_STATUS: `${DOMAIN}api/users/get-status`,
  REPORT: `${DOMAIN}api/reports/get-district-report-data`,
  SURVEY_SLUG: `${DOMAIN}api/surveys/validate-share-url`,
  SURVEY_STATUS: `${DOMAIN}api/surveys/get-status`,
  SURVEY_RESPONSES: `${DOMAIN}api/surveys/get-user-responses`,
  SUBMIT_SURVEY: `${DOMAIN}api/surveys/submit`,
  REPORT_LIST: `${DOMAIN}api/reports/list`,
  DISTRICT_LIST: `${DOMAIN}api/reports/get-district-list`,
  UPDATE_MEMBERS_LIST: `${DOMAIN}api/reports/update-members-list`,
  REPORT_RESOURCES_LIST: `${DOMAIN}api/surveys/get-recommended-resources`,
  SET_SETTINGS: `${DOMAIN}api/reports/set-district-config`,
  SET_PRODUCT_METRICS_WINDOW: `${DOMAIN}api/reports/set-product-metrics-window`,
  CSRF: `${DOMAIN}csrf/get`,
  FBX: 'https://services.google.com/fb/submissions/sherlocktransformationsurvey/',
  OPT_IN: `${DOMAIN}api/users/set-communication-data`,
  AUTHENTICATE: `${DOMAIN}api/users/record-authentication`,
  REPORT_DOWNLOAD: `${DOMAIN}api/reports/download-report`,
};


/**
  * Gets information pertaining to the window
  * @return {!Object} the window's scrollY, width and height properties
  */
export const getScreen = () => ({
  scrollY: window.scrollY,
  width: window.innerWidth,
  height: window.innerHeight,
});

export const LARGE_SCREEN_WIDTH = 1276;
/**
  * Determines if the window is above 1200 pixels
  * @return {boolean} true if above 1200
  */
export const isLargeViewport = () => getScreen().width > LARGE_SCREEN_WIDTH;

/**
  * Determines if the window is above 300 pixels and below isLargeViewport size
  * @return {boolean} true if above 300 and less than isLargeViewport size
  */
export const isSmallViewport = () => {
  const screenWidth = getScreen().width;

  return screenWidth > 768 && !isLargeViewport();
};

/**
 * Window resize event listener that throttles event cb execution
 * @return {undefined}
 *
 * @example
 *
 *     formatDateShort(YYYY-MM-DD)
 */
export function optimizedResize() {
  const throttle = function(type, name, obj) {
    obj = obj || window;
    let running = false;
    const func = function() {
      if (running) {
        return;
      }
      running = true;
      requestAnimationFrame(function() {
        obj.dispatchEvent(new CustomEvent(name));
        running = false;
      });
    };
    obj.addEventListener(type, func);
  };

  /* init - you can init any event */
  throttle('resize', 'optimizedResize');
};

/**
 * Checks if value is a number, and is not NaN.
 *
 * @param {!any} data - the value to check
 * @param {?defaultValue} defaultValue - the value to use if invalid
 * @return {number} returns the value if valid, and 0 if not.
 *
 */
export function setZeroIfInvalidNumber(data, defaultValue) {
  let value = defaultValue || 0;
  if (
    !isNaN(data) &&
    isFinite(data) &&
    typeof(data) === 'number' &&
    data !== undefined
  ) {
    value = data;
  }
  return value;
};

/**
 * Gets access token from Google User
 *
 * @param {!object} googleUser - The Google User object
 * @return {string} The access code
 *
 */
export const getAccessToken = (googleUser) => {
  const accessToken = googleUser.getAuthResponse().access_token;
  return accessToken;
};

/**
 * Gets the sum of numbers in an array
 *
 * @param {!array} array - The array of numbers to add together
 * @return {number} The sum of the array of numbers added
 *
 * @example
 *
 *     getSum(arrayToAdd)
 */
export const getSum = (array) => {
  const sum = array.reduce((a, b) => a + b, 0);
  return sum;
};

/**
 * Formats date from YYYY-MM-DD to MM/DD/YY
 *
 * @param {!date} date - The date to format (YYYY-MM-DD)
 * @return {string} The formatted date as a string (MM/DD/YY)
 *
 * @example
 *
 *     formatDateMicro(YYYY-MM-DD)
 */
export const formatDateMicro = (date) => {
  date = new Date(date.split('-').join('/'));
  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear().toString().substr(2, 2);

  return `${month}/${day}/${year}`;
};

/**
 * Formats date from YYYY-MM-DD to Mon DD, YYY
 *
 * @param {!date} date - The date to format (YYYY-MM-DD)
 * @return {string} The formatted date as a string (Month DD, YYYY)
 *
 * @example
 *
 *     formatDateShort(YYYY-MM-DD)
 */
export const formatDateShort = (date) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ];
  const d = new Date(date.replace('-', '/'));
  const month = d.getMonth();
  const day = d.getDate();
  const year = d.getFullYear();
  if (!months[month] || !day || !year || isNaN(day) || isNaN(year)) {
    return '-';
  }
  return `${months[month]} ${day}, ${year}`;
};

/**
 * Formats date from YYYY-MM-DD > Month Day, Year
 *
 * @param {!date} date - The date to format (YYYY-MM-DD)
 * @return {string} The formatted date as a string (Month DD, YYYY)
 *
 * @example
 *
 *     formatDateLong(YYYY-MM-DD)
 */
export const formatDateLong = (date) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const d = new Date(date.replace('-', '/'));
  const month = d.getMonth();
  const day = d.getDate();
  const year = d.getFullYear();
  if (!months[month] || !day || !year || isNaN(day) || isNaN(year)) {
    return '-';
  }
  return `${months[month]} ${day}, ${year}`;
};

export const formatDateLongNoYear = (date) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const d = new Date(date.replace('-', '/'));
  const month = d.getMonth();
  const day = d.getDate();
  if (!months[month] || !day || isNaN(day)) {
    return '-';
  }
  return `${months[month]} ${day}`;
};

/**
 * Formats date from YYYY-MM-DD > Month Day, Year at Time
 *
 * @param {!date} date - The date to format (YYYY-MM-DD HH:MM:SS)
 * @return {string} The formatted date as a string (Month DD, YYYY at H:MM A)
 *
 * @example
 *
 *     formatDateLong(YYYY-MM-DD HH:MM:SS)
 */
export const formatDatetimeLong = (date) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const d = new Date(date.replace('-', '/'));
  const month = d.getMonth();
  const day = d.getDate();
  const year = d.getFullYear();
  let hours = d.getHours();
  let minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? '0'+minutes : minutes;
  if (!months[month] || !day || !year || isNaN(day) || isNaN(year)) {
    return '-';
  }
  return `${months[month]} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
};

/**
 * Formats date from YYYY-MM-DD > MMM D - MMM D, YYYY add 12 weeks to a date
 *
 * @param {date} dte - date object
 * @return {string} - formatted date string
 */
export const formatDateChartLegend = (dte) => {
  const oldDate = new Date(dte);
  const newDateStart = moment(oldDate).utc().format('MMM D');
  const newDateEnd = moment(oldDate).utc().add(PRODUCT_WINDOW_LENGTH, 'weeks')
      .format('MMM D, YYYY');
  return `${newDateStart} - ${newDateEnd}`;
};


/**
 * Formats javascript Date to YYYY-MM-DD
 *
 * @param {!date} date - The date to format
 * @param {?boolean} ignorePlaceholders - ignore day/month placeholders (0)
 * @return {string} The formatted date as a string (YYYY-MM-DD)
 *
 * @example
 *
 *     convertDateToSimpleDate(new Date())
 */
export const convertDateToSimpleDate = (date, ignorePlaceholders) => {
  let month = '' + (date.getMonth() + 1);
  let day = '' + date.getDate();
  const year = date.getFullYear();

  if (!ignorePlaceholders) {
    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
  }

  return [year, month, day].join('-');
};


/**
 * Unmount D3 Graph node
 *
 * @param {!node} node - The node on which d3 lives that we want to remove
 * @param {!d3} d3 - The d3 instance
 *
 */
export const unMountD3 = (node, d3) => {
  d3.select(node)
      .exit()
      .remove();
};


/**
 * Checks if Form Value object contains a target field's value.
 *
 * @param {string} id - The field that we're looking for
 * @param {!Object} data - The form object we're checking in
 * @return {string} The value already in the form object
 *
 * @example
 *
 *     checkFieldValue('textarea', dataToCheck)
 */
export const checkFieldValue = (id, data) => {
  let value;
  if (data.fieldValues) {
    value = data.fieldValues[`${id}`];
  } else if (data[id]) {
    value = data[id];
  } else {
    value = undefined;
  }
  return value;
};

/**
 * Converts a string to camelCased format
 *
 * @param {string} string - The string to format
 * @return {string} Camel cased string
 *
 * @example
 *
 *     toCamelCase('convert-me-to-camel-case')
 */
export const toCamelCase = (string) => {
  const camelCased = string.replace(/-([a-z])/g, (g) => {
    return g[1].toUpperCase();
  });
  return camelCased;
};

/**
 * Wraps D3 text
 *
 * @param {string} text - String to wrap
 * @param {!d3} d3 - The d3 instance
 *
 * @example
 *
 *     d3WrapText('Very Long Label That Should Wrap', d3)
 */
export const d3WrapText = (text, d3) => {
  text.each(function() {
    // eslint-disable-next-line no-invalid-this
    const text = d3.select(this);
    const words = text.text().split(/\s+/).reverse();
    const lineHeight = 20;
    const width = parseFloat(text.attr('width'));
    const y = parseFloat(text.attr('y'));
    const x = text.attr('x');
    const anchor = text.attr('text-anchor');

    let tspan = text.text(null)
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', anchor);

    let lineNumber = 0;
    let line = [];
    let word = words.pop();

    while (word) {
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node().getComputedTextLength() > width) {
        lineNumber += 1;
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = text.append('tspan')
            .attr('x', x)
            .attr('y', y + lineNumber * lineHeight)
            .attr('anchor', anchor)
            .text(word);
      }
      word = words.pop();
    }
  });
};

/**
 * Gets the average number of an array of numbers
 *
 * @param {!array} array - Array of numbers to average
 * @return {number} Average
 *
 * @example
 *
 *     getAvg([3, 4, 6, 7])
 */
export const getAvg = (array) => {
  if (array.length === 0) {
    return 0;
  }
  let total = 0;
  for (let i = 0; i < array.length; i++) {
    const val = array[i] == undefined ? 0 : array[i];
    total += val;
  }
  const avg = total / array.length;
  return avg;
};

/**
 * Returns largest value in each column of a 2D array.
 * @param {array} arr - Array of arrays.
 * @return {array} Array of largest values at each column.
 */
export const largestInColumn = (arr) => {
  const res = [];
  for (let i=0; i < arr.length; i++) {
    let maxm = arr[0][i];
    for (let j=1; j < arr[i].length; j++) {
      if (arr[j][i] > maxm) {
        maxm = arr[j][i];
      }
    }
    res.push(maxm);
  }
  return res;
};

/**
 * Gets the index of a value based on it's property
 *
 * @param {!object} data - object of data to search
 * @param {string} key - key that we are looking for
 * @param {string} value - value that we are looking for
 * @return {number} index
 *
 * @example
 *
 *     getIndexByProperty(object, 'name', 'john')
 */
export const getIndexByProperty = (data, key, value) => {
  for (let i = 0; i < data.length; i++) {
    if (data[i][key] === value) {
      return i;
    }
  }
  return -1;
};

/**
 * Capitalizes the first letter of a word
 *
 * @param {string} string - String to capitalize
 * @return {string} Capitalized string
 *
 * @example
 *
 *     capitalize('john')
 */
export const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};


/**
 * Gets the remaining percentage of 100% given an intial value.
 *
 * @param {number} percent - decimal to get remainder for
 * @return {!array} initial percentage and the remaining percentage in decimals.
 *
 * @example
 *
 *     getRemainingPercent(.3)
 */
export const getRemainingPercent = (percent) => {
  const remainingPercent = 1 - percent;
  const dataArray = [percent, remainingPercent];
  return dataArray;
};


/**
 * Converts a decimal to a rounded percentage, with an exception in the case
 * of decimals < 0.0095.
 *
 * @param {number} percent - decimal to get percentage for
 * @return {number} decimal in percentage
 *
 * @example
 *
 *     getPercentValue(.3)
 */
export const getPercentValue = (percent) => {
  const isDecimalException = Math.abs(percent) < 0.0095;
  let value = Math.round(percent * 100);
  if (isDecimalException) {
    value = Math.round( (percent * 100) * 10 ) / 10;
  }
  return value;
};


/**
 * Gets the difference in percentage between two decimals.
 *
 * @param {number} decimal1 - first decimal
 * @param {number} decimal2 - second decimal
 * @return {number} difference between the percentages
 *
 * @example
 *
 *     getPercentageDifference(.3, .5)
 */
export const getPercentageDifference = (decimal1, decimal2) => {
  const percentage = (decimal1 - decimal2);
  return getPercentValue(percentage);
};


/**
 * Returns true/false depending if value is array
 *
 * @param {!array} value - decimal to get remainder for
 * @return {boolean} true if value is array
 *
 * @example
 *
 *     isArray([])
 */
export function isArray(value) {
  return Array.isArray(value);
}

/**
 * Returns true/false depending if value is non empty array
 *
 * @param {!array} value - decimal to get remainder for
 * @return {boolean} true if value is non empty array
 *
 * @example
 *
 *     isArrayEmpty([])
 */
export function isArrayEmpty(value) {
  return isArray(value) && !Boolean(value.length);
}

/**
 * Executes cb when space/enter key is pressed
 *
 * @param {!object} e - DOM Event
 * @param {!Function} cb - function to call on space/enter press
 * @return {undefined}
 *
 * @example
 *
 *     handleButtonKeyEvents(e, () => {})
 */
export function handleButtonKeyEvents(e, cb) {
  const key = e.key;

  if (key === ' ' || key === 'Enter') {
    typeof cb === 'function' && cb(e);
  }
}

/**
 * Returns url
 *
 * @param {!object} config - title(string), desc(string), date(20191027T070000Z)
 * @return {string}
 *
 * @example
 *     date = (new Date('10-27-2019'))
 *       .toISOString().replace(/-|:|\.\d\d\d/g,"")
 *     googleCalendarUrl({ title, desc, date })
 */
export function googleCalendarUrl({title, desc, date}) {
  const url = 'https://www.google.com/calendar/render?action=TEMPLATE&text=';
  const calTitle = title;
  const calDesc = `&details=${desc}`;
  const calDateStart = `&dates=${date}`;
  const calDateEnd = `%2F${date}`;

  return url + calTitle + calDesc + calDateStart + calDateEnd;
}


/**
 * Returns string
 *
 * @param {string} name - name or title of chart item
 * @param {boolean} isPrev - if this chart item comes from prior report data
 * @return {string}
 *
 * @example
 *     slug = getChartItemSlug(item.title)
 */

export function getChartItemSlug(name, isPrev) {
  const parts = [name.toLowerCase().split(' ').join('-')];
  if (isPrev) {
    parts.push('prev');
  }
  return parts.join('-');
}


/**
  * Returns string
  *
  * @param {string} string - string to search and replace
  * @param {!Date} date - date to use in placeholder replacement
  * @return {string}
  *
  * @example
  *     const text = 'Replace this: {{report_available_date}}'
  *     const date = new Date();
  *     const replacedText = replaceReportAvailableDatePlaceholder(text, date)
  */

export function replaceReportAvailableDatePlaceholder(string, date) {
  const dateString = formatDateLong(convertDateToSimpleDate(date));
  return string.replace(REPORT_AVAILABLE_DATE_REPLACEMENT, dateString);
}

export function replaceSurveyNamePlaceholder(string, surveyName) {
  return string.replace(SURVEY_NAME_REPLACEMENT, surveyName);
}

export function replaceReportNamePlaceholder(string, reportName) {
  return string.replace(REPORT_NAME_REPLACEMENT, reportName);
}

export function replaceCloseDatePlaceholder(string, closeDate) {
  const dateString = formatDateLongNoYear(convertDateToSimpleDate(closeDate));
  return string.replace(CLOSE_DATE_REPLACEMENT, dateString);
}

/**
  * Runs a callback on the first user touch event
  *
  * @param {!Function} callback
  *
  */
export function createFirstTouchListener(callback) {
  window.addEventListener('touchstart', function onFirstTouch() {
    window.removeEventListener('touchstart', onFirstTouch, false);
    callback();
  }, false);
}

/**
  * Returns true/false if browser is running on private/incognito mode
  *
  * @return {boolean}
  *
  */
export function isPrivateMode() {
  return new Promise((resolve) => {
    const yes = () => resolve(true);
    const no = () => resolve(false);

    const isNavigator = {
      CHROME: /(?=.*(opera|chrome)).*/i.test(navigator.userAgent) &&
      'storage' in navigator && 'estimate' in navigator.storage,
      FIREFOX: 'MozAppearance' in document.documentElement.style,
      MICROSOFTEDGE: !window.indexedDB &&
      (window.PointerEvent ||
        window.MSPointerEvent),
      SAFARI: navigator.userAgent
          .match(/Version\/([0-9\._]+).*Safari/),
    };

    if (!navigator.cookieEnabled) {
      return no();
    }

    const detectChromeOpera = () => {
      const isChromeOpera = isNavigator.CHROME;
      if (isChromeOpera) {
        navigator.storage.estimate().then(({quota}) => {
          const quotaInMib = Math.round(quota / (1024 * 1024));
          quotaInMib < 500 ? yes() : no();
        });
      }
      return !!isChromeOpera;
    };

    const detectFirefox = () => {
      const isMozillaFirefox = isNavigator.FIREFOX;
      if (isMozillaFirefox) {
        if (indexedDB == null) yes();
        else {
          const db = indexedDB.open('inPrivate');
          db.onsuccess = no;
          db.onerror = yes;
        }
      }
      return isMozillaFirefox;
    };

    const detectEdgeIE10 = () => {
      const isEdgeIE10 = isNavigator.MICROSOFTEDGE;
      if (isEdgeIE10) yes();
      return !!isEdgeIE10;
    };

    const detectSafari = () => {
      const isSafari = isNavigator.SAFARI;
      if (isSafari) {
        const testLocalStorage = () => {
          try {
            if (localStorage.length) no();
            else {
              localStorage.setItem('inPrivate', '0');
              localStorage.removeItem('inPrivate');
              no();
            }
          } catch (e) {
            navigator.cookieEnabled ? yes() : no();
          }
          return true;
        };
        const version = parseInt(isSafari[1], 10);
        if (version < 11) return testLocalStorage();
        try {
          window.openDatabase(null, null, null, null);
          no();
        } catch (_) {
          yes();
        }
      }
      return !!isSafari;
    };
    if (detectChromeOpera()) return;
    if (detectFirefox()) return;
    if (detectEdgeIE10()) return;
    if (detectSafari()) return;

    return no();
  });
}


/**
  * Adds timeout to a fetch function
  *
  * @param {!String} resource
  * @param {!Object} options
  * @return {Object}
  *
  */
export async function fetchTimeout(resource, options) {
  const {timeout = 20000} = options;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const response = await fetch(resource, {
    ...options,
    signal: controller.signal,
  });
  clearTimeout(id);
  return response;
}
